import { AnnotationDataBoundingBox } from '../types/models/annotation'
import {
  migrateBoundingBoxToPoints,
  getMinMaxFromPoints,
  normalizedPointsToPixels,
  pointsArrayToStructured,
  getPointDistance,
  getBoundingBoxRotation,
} from './helpers'

interface RenderParams {
  imageSrc: string
  box: AnnotationDataBoundingBox
  canvasSize?: number
  rotateLongSideVertical?: boolean
}

export async function renderSnippetToPNG({
  imageSrc,
  box,
  canvasSize = 512,
  rotateLongSideVertical = false,
}: RenderParams): Promise<Blob> {
  const img = await loadImage(imageSrc)
  const canvas = document.createElement('canvas')
  canvas.width = canvasSize
  canvas.height = canvasSize
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas not supported')

  const migrated = migrateBoundingBoxToPoints(box, img.naturalWidth, img.naturalHeight)

  // Compute center/size using axis-aligned rect
  let bboxWidth = 0
  let bboxHeight = 0
  let bboxCenterX = 0
  let bboxCenterY = 0
  let rotationRadians = 0
  let longLen = 0
  let shortLen = 0

  // Keep a pixel-space copy of OBB points when available
  let pixelPointsForOBB: number[] | null = null
  if (migrated.points && migrated.points.length === 8) {
    // Points may be normalized (0-1) or pixel; detect and convert if needed
    const maxX = Math.max(
      migrated.points[0],
      migrated.points[2],
      migrated.points[4],
      migrated.points[6]
    )
    const maxY = Math.max(
      migrated.points[1],
      migrated.points[3],
      migrated.points[5],
      migrated.points[7]
    )
    const areNormalized = maxX <= 1 && maxY <= 1
    const pixelPoints = areNormalized
      ? normalizedPointsToPixels(migrated.points, img.naturalWidth, img.naturalHeight)
      : [...migrated.points]
    pixelPointsForOBB = pixelPoints
    const { xmin, ymin, xmax, ymax } = getMinMaxFromPoints(pixelPoints)
    bboxWidth = xmax - xmin
    bboxHeight = ymax - ymin
    bboxCenterX = (xmin + xmax) / 2
    bboxCenterY = (ymin + ymax) / 2

    if (rotateLongSideVertical) {
      // Determine current orientation and angle along the long edge
      const verts = pointsArrayToStructured(pixelPoints)
      const topEdgeLen = getPointDistance(verts.topLeft, verts.topRight)
      const leftEdgeLen = getPointDistance(verts.topLeft, verts.bottomLeft)
      longLen = Math.max(topEdgeLen, leftEdgeLen)
      shortLen = Math.min(topEdgeLen, leftEdgeLen)
      const longIsHorizontal = topEdgeLen >= leftEdgeLen
      const angle = getBoundingBoxRotation(pixelPoints)
      // If long edge is horizontal, we want it vertical: rotate by -(angle - 90deg)
      // If long edge is already vertical, rotate by -angle to align to 0/90
      rotationRadians = longIsHorizontal ? Math.PI / 2 - angle : -angle
    }
  } else {
    let xmin = migrated.xmin ?? 0
    let xmax = migrated.xmax ?? 0
    let ymin = migrated.ymin ?? 0
    let ymax = migrated.ymax ?? 0
    // Handle normalized min/max
    if (xmax <= 1 && ymax <= 1) {
      xmin = xmin * img.naturalWidth
      xmax = xmax * img.naturalWidth
      ymin = ymin * img.naturalHeight
      ymax = ymax * img.naturalHeight
    }
    bboxWidth = xmax - xmin
    bboxHeight = ymax - ymin
    bboxCenterX = (xmin + xmax) / 2
    bboxCenterY = (ymin + ymax) / 2

    if (rotateLongSideVertical) {
      // Axis-aligned boxes: long edge horizontal => rotate 90deg around center
      const longIsHorizontal = bboxWidth >= bboxHeight
      longLen = Math.max(bboxWidth, bboxHeight)
      shortLen = Math.min(bboxWidth, bboxHeight)
      rotationRadians = longIsHorizontal ? Math.PI / 2 : 0
    }
  }

  const padding = 1.2
  // Compute zoom and vertical offset based on rotated extents to avoid clipping
  let effectiveWidth = bboxWidth
  let effectiveHeight = bboxHeight
  let zoom = 1
  let deltaImageY = 0
  if (rotateLongSideVertical && pixelPointsForOBB) {
    // Build rotated-space points relative to center
    const cos = Math.cos(rotationRadians)
    const sin = Math.sin(rotationRadians)
    const rel: Array<{ rx: number; ry: number }> = []
    for (let i = 0; i < pixelPointsForOBB.length; i += 2) {
      const rx =
        (pixelPointsForOBB[i] - bboxCenterX) * cos - (pixelPointsForOBB[i + 1] - bboxCenterY) * sin
      const ry =
        (pixelPointsForOBB[i] - bboxCenterX) * sin + (pixelPointsForOBB[i + 1] - bboxCenterY) * cos
      rel.push({ rx, ry })
    }
    let minRx = rel[0].rx,
      maxRx = rel[0].rx,
      minRy = rel[0].ry,
      maxRy = rel[0].ry
    for (let i = 1; i < rel.length; i++) {
      if (rel[i].rx < minRx) minRx = rel[i].rx
      if (rel[i].rx > maxRx) maxRx = rel[i].rx
      if (rel[i].ry < minRy) minRy = rel[i].ry
      if (rel[i].ry > maxRy) maxRy = rel[i].ry
    }
    effectiveWidth = maxRx - minRx
    effectiveHeight = maxRy - minRy
    const zoomX = canvas.width / (effectiveWidth * padding)
    const zoomY = canvas.height / (effectiveHeight * padding)
    zoom = Math.min(zoomX, zoomY)
    const marginCanvasY = Math.max(0, (canvas.height - effectiveHeight * zoom) / 2)
    const yMinCanvas = canvas.height / 2 + zoom * minRy
    const deltaCanvasY = marginCanvasY - yMinCanvas
    deltaImageY = deltaCanvasY / zoom
  } else {
    const zoomX = canvas.width / (bboxWidth * padding)
    const zoomY = canvas.height / (bboxHeight * padding)
    zoom = Math.min(zoomX, zoomY)
  }

  // Draw image
  ctx.save()
  ctx.translate(canvas.width / 2, canvas.height / 2)
  if (rotateLongSideVertical && rotationRadians !== 0) {
    ctx.rotate(rotationRadians)
  }
  ctx.scale(zoom, zoom)
  ctx.translate(-bboxCenterX, -bboxCenterY)
  if (rotateLongSideVertical) {
    ctx.translate(0, deltaImageY)
  }
  ctx.drawImage(img, 0, 0)
  ctx.restore()

  // Overlays removed

  const blob = await new Promise<Blob>((resolve) =>
    canvas.toBlob((b) => resolve(b as Blob), 'image/png')
  )
  return blob
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    // crossOrigin not required for same-origin/object/data URLs
    img.onload = () => resolve(img)
    img.onerror = (e) => reject(e)
    img.src = url
  })
}
